import { css } from '@emotion/react';
import { Locale } from '@flysas-tech/book-value-stream-models';
import { Paragraph, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import { t, Trans } from 'i18n';
import { reverseLocale } from 'shared';

import { buildMarketLink } from '../../utils/buildMarketLink';

const styles = {
  codeShareDisclaimer: css`
    padding: ${tokens.dimension['space-3']};
    flex-direction: row;
    display: flex;
    gap: ${tokens.dimension['space-3']};
  `,
  codeShareContainer: css`
    display: flex;
    gap: ${tokens.dimension['space-4']};
    flex-direction: column;
    color: ${semanticTokens.color.text.primary};
    margin-left: ${tokens.dimension['space-4']};
  `,
  infoIcon: css`
    flex-shrink: 0;
    margin-left: ${tokens.dimension['space-4']};
    color: ${semanticTokens.color.icon['accent-01']};
  `,
  codeShareHeader: css`
    font-weight: 700;
  `,
  hasMiddleSeatFreeDisclaimer: css`
    padding: ${tokens.dimension['space-3']};
    flex-direction: row;
    display: flex;
    gap: ${tokens.dimension['space-3']};
  `,
  hasMiddleSeatFreeContainer: css`
    display: flex;
    gap: ${tokens.dimension['space-4']};
    flex-direction: column;
    color: ${semanticTokens.color.text.primary};
    margin-left: ${tokens.dimension['space-4']};
  `,
};

export const FlightDisclaimer = ({ market }: { market: string }) => {
  const isUs = market === reverseLocale(Locale.EN_US);
  const path = t('upsell.optionalServiceDisclaimer.link', { defaultValue: '/legal-info/optional-services-fees/' });
  const link = buildMarketLink(market, path);

  return (
    <>
      {isUs && (
        <p>
          <Trans i18nKey="rebooking.disclaimerUnitedStates">
            This fare is bilaterally agreed between Scandinavian Airlines System (SAS) and the selected airline(s).To
            ensure that you get this fare, the reservation should be made now.Taxes including the September 11th
            Security Fee are included except where local airport taxes are collected at check-in time.Unless you select
            a one-way trip on the prior page, fares are each way based on required roundtrip purchase.SAS Business
            passengers (class codes C, D, Z & I) on SAS transatlantic flight itineraries will be seated in the SAS
            Business cabin on the transatlantic flight& in premium Economy (SAS Plus) on SAS-operated connecting
            flights, if any, within Scandinavia and Europe.Business class may not be available on SAS codeshare flights
            operated by other carriers and on connecting flights operated by other carriers.SAS Plus (class codes Y, S,
            B & P) is available on SAS-operated flights. This service is not available on SAS codeshare flights operated
            by other carriers & on connecting flights operated by other carriers
          </Trans>
        </p>
      )}

      <p>
        <Trans
          i18nKey="rebooking.disclaimer"
          components={{ anchorLink: <a href={link} target="_blank" rel="noreferrer" /> }}
          defaults={`• Displayed prices apply to one adult including taxes & carrier-imposed fees. • The above products, rules and services apply on SAS-operated flights. • Products and services offered by our partner airlines may differ. • Lounge access and Fast Track are included in SAS Plus and SAS Business at the airports where they are available. • SAS Plus is not available on SAS codeshare flights or connecting flights operated by other carriers. • <1>Additional baggage fees may apply</1>. • Outbound and return fares are interdependent and subject to change. • The ticket is only valid for the sequence of transportation shown on it. More info in Conditions of Carriage, Section 4.5.`}
        />
      </p>
    </>
  );
};

export const CodeShareDisclaimer = () => {
  return (
    <div css={styles.codeShareDisclaimer}>
      <div css={styles.codeShareContainer}>
        <Paragraph css={styles.codeShareHeader} color={'primary'} size={'paragraph-2'}>
          *{' '}
          {t('common:partnerAirlinesDisclaimerHeader', {
            defaultValue: 'Your trip includes one or more partner airlines',
          })}
        </Paragraph>
        <Paragraph color={'primary'} size={'paragraph-2'}>
          {t('common:partnerAirlinesDisclaimer', {
            defaultValue:
              'Products and services may differ from those offered by SAS – such as baggage rules and onboard service.',
          })}
        </Paragraph>
      </div>
    </div>
  );
};

export const HasMiddleSeatFreeDisclaimer = () => {
  return (
    <div css={styles.hasMiddleSeatFreeDisclaimer}>
      <div css={styles.hasMiddleSeatFreeContainer}>
        <Paragraph color={'primary'} size={'paragraph-2'}>
          **{' '}
          {t('common:middleSeatFreeDisclaimer', {
            defaultValue:
              'Middle seat free available on international sectors only. Not available on domestic connection flights.',
          })}
        </Paragraph>
      </div>
    </div>
  );
};
